import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'; // useFormContext used in ProcedureItem

import { caseSharedProps } from 'types/types';
import { CaseProcedureRow } from './CaseProcedureRaw';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import SelectCPTs from 'components/modals/SelectCPTs/SelectCPTs';
import SelectCPTModifier from 'components/modals/SelectCPTModifier/SelectCPTModifier';
import CustomText from 'components/common/CustomText/CustomText';

interface Props {
  shared: caseSharedProps;
  defaultProcedureDate?: dayjs.Dayjs;
}

/* ---------------------------------------------------------
   CHILD COMPONENT — SAFE PLACE FOR HOOKS
--------------------------------------------------------- */

interface ProcedureItemProps {
  field: any;
  index: number;
  remove: (index: number) => void;
  disableRemove: boolean;
  shared: caseSharedProps;
}

function ProcedureItem({
  field,
  index,
  remove,
  disableRemove,
  shared,
}: ProcedureItemProps) {
  const { control, setValue } = useFormContext();

  const procedureName = useWatch({
    control,
    name: `case_procedures.${index}.procedure_name`,
  });

  const cpts = useWatch({
    control,
    name: `case_procedures.${index}.cpts`,
  });

  const cptModifier = useWatch({
    control,
    name: `case_procedures.${index}.cpt_modifier`,
  });

  return (
    <>
      <CaseProcedureRow
        index={index}
        remove={remove}
        disableRemove={disableRemove}
        shared={shared}
      />

      <Box>
        <SelectCPTs
          name={`case_procedures.${index}.cpts`}
          title="CPT Codes and RVUs"
          procedureNamePath={`case_procedures.${index}.procedure_name`}
        />
      </Box>

      {Array.isArray(cpts) && cpts.length > 0 && (
        <Box>
          <SelectCPTModifier
            title="CPT Modifier"
            selectedModifier={cptModifier}
            setCPTModifier={(modifier) => {
              setValue(`case_procedures.${index}.cpt_modifier`, modifier, {
                shouldDirty: true,
              });
              setValue(`case_procedures.${index}.cpt_modifier_id`, modifier?.id ?? null, {
                shouldDirty: true,
              });
            }}
            cpts={cpts}
          />
        </Box>
      )}
    </>
  );
}

/* ---------------------------------------------------------
   PARENT COMPONENT
--------------------------------------------------------- */

export default function CaseProcedure({
  shared,
  defaultProcedureDate,
}: Props) {

  const {
    fields: procedureFields,
    append,
    remove,
  } = useFieldArray({
    name: 'case_procedures',
  });

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {procedureFields.map((field, index) => (
        <ProcedureItem
          key={field.id}
          field={field}
          index={index}
          remove={remove}
          disableRemove={procedureFields.length === 1}
          shared={shared}
        />
      ))}

      <Box display="flex" justifyContent="center" alignItems="center">
        <CustomButton
          text="Add another procedure"
          onClick={() =>
            append({
              id: null,
              date_of_procedure: defaultProcedureDate ?? dayjs(),
              procedure_name: '',
              type: '',
              approach: '',
              position: '',
              minimally_invasive: '',
              navigation: '',
              robotic: '',
              indication: '',
              side: '',
              target: '',
              nerve: '',
              location: '',
              _procedure: '',
              tici_grade: '',
              osteotomy: '',
              _fusion: '',
              interbody_fusion: '',
              extension_to_pelvis: '',
              fusion_levels: '',
              decompression_levels: '',
              morphogenic_protein: '',
              bone_graft: '',
              implant: '',
              implant_type: '',
              _vendors: '',
              vendors: '',
              access: '',
              vascular_closure_device: '',
              duration_of_radiation: '',
              duration_of_procedure: '',
              findings: '',
              complications: '',
              outcome: '',
              cpts: [],
              cpt_modifier: null,
              cpt_modifier_id: null,
            })
          }
        />
      </Box>
    </Box>
  );
}
